/**
 * CustomDataGridFilterPanel
 * -------------------------
 * This component provides a custom filter panel for a MUI DataGrid.
 * - It allows users to add, remove, and configure multiple filters for grid columns.
 * - Supports different filter operators for date, string, number, and boolean columns.
 * - Handles logic operators (AND/OR) between filters.
 * - Applies, resets, and manages filter state, syncing with the DataGrid's filter model.
 * - Uses memoization for performance and clean mapping for operator labels.
 */
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { Box, Button, Divider, FormControl, IconButton, InputLabel, MenuItem, Select } from "@mui/material";
import { GridFilterModel, GridLogicOperator } from "@mui/x-data-grid";
import React from "react";
import { getOperatorLabel } from "../../Utils/js-common";
import { CustomDataGridFilterLogicOperatorDropdown } from "./CustomDataGridFilterLogicOperatorDropdown";
import { CustomDataGridFilterValueField } from "./CustomDataGridFilterValueField";
import { CustomDataGridFilterPanelProps, FilterId } from "../Interfaces/CustomDataGrid.interfaces";

const CustomDataGridFilterPanel = (props: CustomDataGridFilterPanelProps) => {
  const visibleColumns = React.useMemo(
    () => props.apiRef.current?.getVisibleColumns()?.filter((column) => column.filterable) ?? [],
    [props.apiRef]
  );
  const firstVisibleColumnField = React.useMemo(() => visibleColumns?.[0]?.field ?? "", [visibleColumns]);
  const initialColumnField = props.defaultColumnField || firstVisibleColumnField;
  const initialVisibleColumnOperator = visibleColumns?.find(c => c.field === initialColumnField)?.filterOperators?.[0]?.value ?? "";
  const [tempFilterModel, setTempFilterModel] = React.useState<GridFilterModel>(() => {
    if (props.filterModel.items.length === 0 && initialColumnField) {
      return {
        ...props.filterModel,
        items: [
          {
            id: crypto.randomUUID(),
            field: initialColumnField,
            operator: initialVisibleColumnOperator,
            value: ""
          }
        ]
      };
    }
    return {
      ...props.filterModel,
      items: props.filterModel.items.map((item) => ({
        ...item,
        id: item.id ?? crypto.randomUUID()
      }))
    };
  });

  const handleFilterChange = React.useCallback((id: FilterId, field: string, operator: string, value: any) => {
    setTempFilterModel((prevModel) => {
      const updatedItems = prevModel.items.map((item) => (item.id === id ? { ...item, field, operator, value } : item));
      return { ...prevModel, items: updatedItems };
    });
  }, []);

  const handleAddFilter = React.useCallback(() => {
    const selectedColumn = visibleColumns?.find(({ field }) => field === initialColumnField);
    const firstOperator = selectedColumn?.filterOperators?.[0]?.value ?? "";
    setTempFilterModel((prevModel) => ({
      ...prevModel,
      items: [
        ...prevModel.items,
        {
          id: crypto.randomUUID(),
          field: initialColumnField,
          operator: firstOperator,
          value: ""
        }
      ]
    }));
  }, [visibleColumns, initialColumnField]);

  const handleDeleteFilter = React.useCallback(
    (id: FilterId) => {
      if (tempFilterModel.items.length === 1) {
        handleReset();
      } else {
        setTempFilterModel((prevModel) => ({
          ...prevModel,
          items: prevModel.items.filter((item) => item.id !== id)
        }));
      }
    },
    [tempFilterModel.items.length]
  );

  const handleApply = React.useCallback(() => {
    props.onFilterModelChange(tempFilterModel);
    handleClose();
  }, [props.onFilterModelChange, tempFilterModel]);

  const handleReset = React.useCallback(() => {
    const resetFilterModel: GridFilterModel = {
      items: []
    };
    setTempFilterModel(resetFilterModel);
    props.onFilterModelChange({ items: [] });
    if (tempFilterModel.items.length === 1) {
      handleClose();
    }
  }, [visibleColumns, firstVisibleColumnField, props.onFilterModelChange, tempFilterModel.items.length]);

  const handleClose = React.useCallback(() => {
    props.apiRef.current?.hideFilterPanel();
  }, [props.apiRef]);

  const handleLogicOperatorChange = (logicOperator: GridLogicOperator) => {
    setTempFilterModel((prev) => ({
      ...prev,
      logicOperator
    }));
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", p: 1, px: 2 }}>
      <Box sx={{ maxHeight: 400, overflowY: "auto", mb: 1 }}>
        {tempFilterModel.items.map((filter, idx) => (
          <Box
            key={filter.id}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              mb: 1,
              mt: idx > 0 ? 0 : 1
            }}
          >
            <IconButton aria-label="delete" onClick={() => handleDeleteFilter(filter.id)} size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
            {idx === 0 && tempFilterModel.items.length > 1 && <Box sx={{ width: 80 }} />}
            {idx > 0 && (
              <CustomDataGridFilterLogicOperatorDropdown
                disabled={idx > 1}
                onChange={handleLogicOperatorChange}
                value={tempFilterModel.logicOperator ?? GridLogicOperator.And}
              />
            )}
            <FormControl size="small" sx={{ width: 200 }}>
              <InputLabel>Columns</InputLabel>
              <Select
                label="Columns"
                value={filter.field}
                onChange={(e) => {
                  const selectedField = e.target.value;
                  const selectedColumn = visibleColumns?.find(({ field }) => field === selectedField);
                  const firstOperator = selectedColumn?.filterOperators?.[0]?.value ?? "";
                  handleFilterChange(filter.id, selectedField, firstOperator, filter.value);
                }}
              >
                {visibleColumns?.map(({ field, headerName }) => (
                  <MenuItem key={field} value={field}>
                    {headerName ?? field}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ width: 150 }}>
              <InputLabel>Operator</InputLabel>
              <Select
                label="Operator"
                value={filter.operator}
                onChange={(e) => handleFilterChange(filter.id, filter.field, e.target.value, filter.value)}
              >
                {visibleColumns
                  ?.find(({ field }) => field === filter.field)
                  ?.filterOperators?.filter((item) => item.value !== "isAnyOf")
                  ?.map((operatorItem) => {
                    const column = visibleColumns?.find(({ field }) => field === filter.field);
                    const operatorValue = operatorItem.value;
                    return (
                      <MenuItem key={operatorValue} value={operatorValue}>
                        {getOperatorLabel(column, operatorValue, operatorItem)}
                      </MenuItem>
                    );
                  })}
              </Select>
            </FormControl>
            <CustomDataGridFilterValueField
              filter={filter}
              visibleColumns={visibleColumns}
              handleFilterChange={handleFilterChange}
            />
          </Box>
        ))}
      </Box>
      <Divider sx={{ mb: 1 }} />
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center"
        }}
      >
        <Button
          startIcon={<AddIcon />}
          onClick={handleAddFilter}
          sx={{ color: "#1976d2", fontWeight: 600, textTransform: "none" }}
        >
          Add Filter
        </Button>
        <Button
          startIcon={<DeleteOutlineIcon />}
          onClick={handleReset}
          sx={{ color: "#1976d2", fontWeight: 600, textTransform: "none" }}
        >
          Remove All
        </Button>
        <Button
          variant="contained"
          onClick={handleApply}
          sx={{
            background: "#1976d2",
            color: "#fff",
            fontWeight: 600,
            textTransform: "none"
          }}
        >
          Apply
        </Button>
      </Box>
    </Box>
  );
};

export default CustomDataGridFilterPanel;
